import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state';
import { runContentBlockAction, moveContentBlockTo } from './contentEditorCommands.js';

export const CONTENT_BLOCK_DRAG_TYPE = 'application/x-mts-content-block-type';
export const CONTENT_BLOCK_MOVE_TYPE = 'application/x-mts-content-block-move';

function handleIcon(kind) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  for (const [key, value] of Object.entries({width:'16',height:'16',viewBox:'0 0 24 24',fill:'none',stroke:'currentColor','stroke-width':'2','aria-hidden':'true'})) svg.setAttribute(key, value);
  if (kind === 'plus') { const path = document.createElementNS(ns, 'path'); path.setAttribute('d', 'M12 5v14M5 12h14'); svg.appendChild(path); }
  else for (const [x,y] of kind === 'grip' ? [[9,5],[15,5],[9,12],[15,12],[9,19],[15,19]] : [[5,12],[12,12],[19,12]]) { const dot=document.createElementNS(ns,'circle');dot.setAttribute('cx',x);dot.setAttribute('cy',y);dot.setAttribute('r','1');svg.appendChild(dot); }
  return svg;
}

function selectedPosition(editor) {
  const selected = editor.state.selection;
  return selected instanceof NodeSelection ? selected.from : selected.$from.depth ? selected.$from.before(selected.$from.depth) : null;
}

export function createContentEditorInteractions(getHandlers) {
  return Extension.create({
    name: 'contentEditorInteractions',
    addKeyboardShortcuts() {
      const action = (name) => () => runContentBlockAction(this.editor, selectedPosition(this.editor), name);
      return {
        'Mod-Shift-d': action('duplicate'),
        'Mod-Shift-Backspace': action('delete'),
        'Alt-Shift-ArrowUp': action('move-up'),
        'Alt-Shift-ArrowDown': action('move-down'),
        'Alt-Enter': () => { if (!this.editor.isEditable) return false; getHandlers().openInsertion(); return true; },
      };
    },
    addProseMirrorPlugins() {
      let dragSource = null;
      const nodeKeys = new WeakMap();
      let nextNodeKey = 0;
      return [new Plugin({ key: new PluginKey('mtsContentEditorInteractions'), view: view => {
        // The rail lives outside the document, including for nested boundaries.
        // Measure presentation only; never dispatch a content transaction.
        let frame;
        const hover = event => {
          const anchors = [...view.dom.querySelectorAll('.mts-editor-insertion-point')];
          const direct = event.target.closest?.('.mts-editor-insertion-point');
          // Later matches are descendants of earlier containing block views.
          const active = direct || anchors.filter(anchor => anchor.nextElementSibling?.contains(event.target)).at(-1);
          for (const anchor of anchors) {
            if (anchor === active) anchor.dataset.hovered = 'true';
            else delete anchor.dataset.hovered;
          }
          if (active) view.dom.dataset.hoveringHandles = 'true';
          else delete view.dom.dataset.hoveringHandles;
        };
        const leave = () => {
          delete view.dom.dataset.hoveringHandles;
          for (const anchor of view.dom.querySelectorAll('[data-hovered]')) delete anchor.dataset.hovered;
        };
        const place = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const bounds = view.dom.getBoundingClientRect();
            const left = bounds.left;
            view.dom.dataset.canvasOffscreen = String(bounds.height > 0 && (bounds.bottom <= 0 || bounds.top >= window.innerHeight));
            const workspace = view.dom.closest('.mts-content-editor-workspace');
            const panel = workspace?.querySelector('.mts-editor-navigation-panel');
            const available = left - Math.max(workspace?.getBoundingClientRect().left || 0, panel?.getBoundingClientRect().right || 0);
            view.dom.dataset.compactHandles = String(available < 84);
            if (!view.dom.querySelector('[data-hovered]')) delete view.dom.dataset.hoveringHandles;
            for (const anchor of view.dom.querySelectorAll('.mts-editor-insertion-point')) {
              anchor.style.setProperty('--mts-editor-handle-left', `${left - anchor.getBoundingClientRect().left - 84}px`);
            }
          });
        };
        const observer = new ResizeObserver(place);
        observer.observe(view.dom);
        window.addEventListener('scroll', place, true);
        view.dom.addEventListener('pointerover', hover);
        view.dom.addEventListener('pointerleave', leave);
        place();
        return { update: place, destroy: () => { observer.disconnect(); window.removeEventListener('scroll', place, true); cancelAnimationFrame(frame); view.dom.removeEventListener('pointerover', hover); view.dom.removeEventListener('pointerleave', leave); } };
      }, props: {
        decorations: (state) => {
          if (!this.editor.isEditable) return null;
          const allowedParents = new Set(getHandlers().insertionParents?.() || []);
          const decorations = [];
          state.doc.descendants((node, position, parent) => {
            if (!node.isBlock || !allowedParents.has(parent.type.name)) return;
            if (!nodeKeys.has(node)) nodeKeys.set(node, ++nextNodeKey);
            const movable = Boolean(getHandlers().canMove?.(node, position));
            decorations.push(Decoration.widget(position, () => {
              const anchor = document.createElement('span');
              anchor.className = 'mts-editor-insertion-point';
              const button = document.createElement('button');
              button.type = 'button'; button.appendChild(handleIcon('plus'));
              button.setAttribute('aria-label', `Add block before ${node.type.name}`);
              button.contentEditable = 'false';
              button.addEventListener('mousedown', (event) => event.preventDefault());
              button.addEventListener('click', () => getHandlers().openInsertion({ position, parentType: parent.type.name }));
              button.addEventListener('dragover', (event) => { if (event.dataTransfer?.types.includes(CONTENT_BLOCK_DRAG_TYPE)) { event.preventDefault(); anchor.dataset.dragActive = 'true'; } });
              button.addEventListener('dragleave', () => { delete anchor.dataset.dragActive; });
              button.addEventListener('drop', (event) => {
                const type = event.dataTransfer?.getData(CONTENT_BLOCK_DRAG_TYPE);
                if (!type) return;
                event.preventDefault(); event.stopPropagation(); delete anchor.dataset.dragActive;
                getHandlers().dropBlock(type, position, parent.type.name);
              });
              anchor.appendChild(button);
              const move = document.createElement('button');
              move.type = 'button'; move.appendChild(handleIcon('grip')); move.contentEditable = 'false';
              move.setAttribute('aria-label', `Drag ${node.type.name}: ${node.textContent.slice(0, 60) || node.type.name}`);
              move.draggable = movable;
              move.disabled = !move.draggable;
              move.addEventListener('dragstart', (event) => {
                const currentNode = this.editor.state.doc.nodeAt(position);
                if (!currentNode || !getHandlers().canMove?.(currentNode, position)) { event.preventDefault(); return; }
                event.stopPropagation();
                dragSource = { position, document: this.editor.state.doc };
                event.dataTransfer.setData(CONTENT_BLOCK_MOVE_TYPE, 'move'); event.dataTransfer.effectAllowed = 'move';
              });
              move.addEventListener('dragend', () => { dragSource = null; for (const active of this.editor.view.dom.querySelectorAll('[data-drag-active]')) delete active.dataset.dragActive; });
              button.addEventListener('dragover', (event) => {
                if (dragSource && event.dataTransfer?.types.includes(CONTENT_BLOCK_MOVE_TYPE)) { event.preventDefault(); event.stopPropagation(); anchor.dataset.dragActive = 'true'; }
              });
              button.addEventListener('drop', (event) => {
                if (!dragSource || !event.dataTransfer?.types.includes(CONTENT_BLOCK_MOVE_TYPE)) return;
                event.preventDefault(); event.stopPropagation(); delete anchor.dataset.dragActive;
                const source = dragSource; dragSource = null;
                if (source.document === this.editor.state.doc) moveContentBlockTo(this.editor, source.position, position);
              });
              anchor.appendChild(move);
              const actions = document.createElement('button');
              actions.type = 'button'; actions.contentEditable = 'false';
              actions.appendChild(handleIcon('menu'));
              actions.setAttribute('aria-label', `Block actions for ${node.type.name}: ${node.textContent.slice(0,60) || node.type.name}`);
              actions.setAttribute('aria-haspopup', 'menu');
              actions.addEventListener('mousedown', event => event.preventDefault());
              actions.addEventListener('click', () => getHandlers().openBlockActions?.({ position, anchor: actions }));
              anchor.appendChild(actions);
              if (selectedPosition(this.editor) === position) anchor.dataset.selected = 'true';
              return anchor;
            }, { side: -1, key: `insert:${position}:${parent.type.name}:${nodeKeys.get(node)}:${movable}:${state.selection.from >= position && state.selection.to <= position + node.nodeSize}`, stopEvent: () => true }));
          });
          return DecorationSet.create(state.doc, decorations);
        },
        handleKeyDown: (_view, event) => {
          if (event.isComposing || _view.composing || event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || !this.editor.isEditable) return false;
          const selection = this.editor.state.selection;
          if (!selection.empty || selection.$from.parent.textContent) return false;
          getHandlers().openInsertion();
          return true;
        },
        handleDrop: (view, event, moved) => {
          if (moved || !this.editor.isEditable) return false;
          const type = event.dataTransfer?.getData(CONTENT_BLOCK_DRAG_TYPE);
          if (!type) return false;
          event.preventDefault();
          const position = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.to;
          getHandlers().dropBlock(type, position);
          return true;
        },
      } })];
    },
  });
}
